import React, { FC, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { ROUTE } from '~/app/routes';
import { CopyInput, Details, Loading, PageHeader, RenderFields } from '~/components';
import { useAccountNumber, useBankAccount } from '~/hooks';
import { useTransfer } from '~/hooks/useTransfers';
import { useNavigate } from '~/lib/navigation';
import { CryptoTransfer, convertTransferAmount } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';

import { InfoForm, TransferPageHeader } from './_components/Layout';

interface Params {
  id: string;
}

const formatData = (transfer: CryptoTransfer): Record<string, unknown> => {
  const formatted: Record<string, unknown> = {};
  Object.entries(transfer).forEach(([key, value]) => {
    formatted[key] = value;
  });
  return formatted;
};

export const PageTransfersCrypto: FC = () => {
  const { id } = useParams<keyof Params>() as Params;
  const navigate = useNavigate();
  const { addDangerNotification } = useNotificationStore();

  const { isLoading: isBankAccountLoading, createRequest: fetchBankAccount } = useBankAccount({
    onError: (error) => {
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  const { isLoading: isAccountNumberLoading, createRequest: fetchAccountNumber } = useAccountNumber({
    onError: (error) => {
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  const {
    response: transfer,
    isLoading: isTransferLoading,
    createRequest: fetchTransfer,
  } = useTransfer<CryptoTransfer>('crypto')({
    initialParams: { id },
    onSuccess: async (response) => {
      fetchAccountNumber({ id: response.accountNumberId });

      try {
        if (response.bankAccountId) {
          await fetchBankAccount({ id: response.bankAccountId });
        }
      } catch (error) {
        addDangerNotification({ content: (error as Error).message });
      }
    },

    onError: (error) => {
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  useEffect(() => {
    if (id) {
      fetchTransfer({ id });
    }
  }, [id]);

  const formattedTransfer = useMemo(() => (transfer ? formatData(transfer) : undefined), [transfer]);

  const formMethods = useForm<CryptoTransfer>();

  useEffect(() => {
    if (transfer) {
      formMethods.setValue('network', transfer.network || '-');
      formMethods.setValue('asset', transfer.asset || '-');
      formMethods.setValue('cryptoAmount', transfer.cryptoAmount || '-');
      formMethods.setValue('senderAddress', transfer.senderAddress || '-');
      formMethods.setValue('recipientAddress', transfer.recipientAddress || '-');
      formMethods.setValue('transactionHash', transfer.transactionHash || '-');
    }
  }, [transfer, formMethods]);

  const isDataLoading = useMemo(() => {
    return isAccountNumberLoading || isBankAccountLoading || isTransferLoading;
  }, [isAccountNumberLoading, isBankAccountLoading, isTransferLoading]);

  return (
    <>
      <PageHeader text="Transfers" backButton />

      <Loading isLoading={isDataLoading} fullHeight>
        {transfer && (
          <TransferPageHeader
            amount={transfer.amount!}
            createdAt={transfer.createdAt}
            isAdding={transfer ? convertTransferAmount('crypto', transfer.isIncoming) > 0 : undefined}
            direction={{ type: transfer.isIncoming ? 'incoming' : 'outgoing' }}
            status={transfer.status!}
            transferId={transfer.id!}
            transferType="crypto"
          />
        )}
        <FormProvider {...formMethods}>
          <InfoForm autoComplete="off">
            {RenderFields<CryptoTransfer>({
              sections: [
                {
                  headline: 'Information',
                  fields: [
                    {
                      id: 'network',
                      label: 'Network',
                      children: ({ value }) => {
                        return <CopyInput value={String(value)} />;
                      },
                    },
                    {
                      id: 'asset',
                      label: 'Asset',
                      children: ({ value }) => {
                        return <CopyInput value={String(value)} />;
                      },
                    },
                    {
                      id: 'cryptoAmount',
                      label: 'Crypto Amount',
                      children: ({ value }) => {
                        return <CopyInput value={String(value)} />;
                      },
                    },
                    {
                      id: 'senderAddress',
                      label: 'Sender Address',
                      children: ({ value }) => {
                        return <CopyInput value={String(value)} isMonospaced />;
                      },
                    },
                    {
                      id: 'recipientAddress',
                      label: 'Recipient Address',
                      children: ({ value }) => {
                        return <CopyInput value={String(value)} isMonospaced />;
                      },
                    },
                    {
                      id: 'transactionHash',
                      label: 'Transaction Hash',
                      children: ({ value }) => {
                        return <CopyInput value={value ? String(value) : '-'} isMonospaced />;
                      },
                    },
                  ],
                },
              ],
            })}
            <Details
              label="Transfer Details"
              formattedData={formattedTransfer}
              rawData={transfer as Record<string, unknown> | undefined}
            />
          </InfoForm>
        </FormProvider>
      </Loading>
    </>
  );
};
